import React, { useState, useEffect, useRef } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import { X, UploadCloud } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import callApi from "Common/utils/apiCaller";

const MediaForm = () => {
  document.title = "Add Media | SuperSalesIQ";
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [mediaData, setMediaData] = useState<any>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Check if we're in edit mode (URL contains an ID)
    const searchParams = new URLSearchParams(location.search);
    const mediaId = searchParams.get("id");
    
    if (mediaId) {
      setIsEdit(true);
      fetchMediaData(mediaId);
    }
  }, [location]);

  const fetchMediaData = async (mediaId: string) => {
    setLoading(true);
    try {
      const res = await callApi(`v1/media/${mediaId}`, "get");
      if (res && res.data && res.data.data) {
        setMediaData(res.data.data);
        setImagePreview(res.data.data.url || null);
        validation.setValues({
          title: res.data.data.title || "",
          alt_text: res.data.data.alt_text || "",
          description: res.data.data.description || "",
        });
      }
    } catch (error) {
      console.error("Error fetching media data:", error);
      toast("Failed to fetch media data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files?.[0] || null;
    if (file) {
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const uploadMedia = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await callApi('v1/media/upload', 'post', formData);
      if (res && res.data && res.data.url) {
        return res.data.url;
      }
      throw new Error('Failed to upload media');
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  };

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: "",
      alt_text: "",
      description: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Please Enter Title"),
      alt_text: Yup.string().required("Please Enter Alt Text"),
      description: Yup.string(),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        let mediaUrl = mediaData?.url || '';
        
        // Upload new file if selected
        if (selectedFile) {
          mediaUrl = await uploadMedia(selectedFile);
        } else if (!isEdit || !mediaUrl) {
          toast("Please select a media file");
          setLoading(false);
          return;
        }
        
        if (isEdit) {
          // Update existing media
          const res = await callApi(`v1/media/${mediaData._id}/update`, "post", {
            media: {
              title: values.title,
              alt_text: values.alt_text,
              description: values.description,
              url: mediaUrl,
            },
          });
          
          if (res && res.data && res.data.status === "Success") {
            toast("Media updated successfully");
            setTimeout(() => {
              navigate("/media");
            }, 1500);
          } else {
            toast("Failed to update media");
          }
        } else {
          // Create new media
          const res = await callApi(`v1/media/create`, "post", {
            media: {
              title: values.title,
              alt_text: values.alt_text,
              description: values.description,
              url: mediaUrl,
            },
          });
          
          if (res && res.data && res.data.status === "Success") {
            toast("Media created successfully");
            setTimeout(() => {
              navigate("/media");
            }, 1500);
          } else {
            toast("Failed to create media");
          }
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <React.Fragment>
      <BreadCrumb title={isEdit ? "Edit Media" : "Add Media"} pageTitle="Media" />
      <ToastContainer closeButton={false} limit={1} />
      
      <div className="grid grid-cols-12 gap-x-5">
        <div className="col-span-12 card 2xl:col-span-12">
          <div className="card-body">
            <div className="flex items-center">
              <h6 className="text-15 grow">{isEdit ? "Edit Media" : "Add Media"}</h6>
              <div className="shrink-0">
                <Link
                  to="/media"
                  className="text-slate-500 btn bg-slate-200 border-slate-200 hover:text-slate-600 hover:bg-slate-300 hover:border-slate-300 focus:text-slate-600 focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100 dark:bg-zink-600 dark:hover:bg-zink-500 dark:border-zink-600 dark:hover:border-zink-500 dark:text-zink-200 dark:ring-zink-400/20"
                >
                  <X className="inline-block size-4" />{" "}
                  <span className="align-middle">Cancel</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="card-body">
            {loading && !validation.isSubmitting ? (
              <div className="flex items-center justify-center h-60">
                <TailSpin visible={true} height="40" width="40" color="#4fa94d" />
              </div>
            ) : (
              <form
                className="create-form"
                onSubmit={validation.handleSubmit}
              >
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="title"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Title*
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter title"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.title}
                    />
                    {validation.touched.title &&
                    validation.errors.title ? (
                      <p className="text-red-500">
                        {validation.errors.title}
                      </p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-6">
                    <label
                      htmlFor="alt_text"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Alt Text*
                    </label>
                    <input
                      type="text"
                      id="alt_text"
                      name="alt_text"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter alt text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.alt_text}
                    />
                    {validation.touched.alt_text &&
                    validation.errors.alt_text ? (
                      <p className="text-red-500">
                        {validation.errors.alt_text}
                      </p>
                    ) : null}
                  </div>
                  <div className="xl:col-span-12">
                    <label
                      htmlFor="description"
                      className="inline-block mb-2 text-base font-medium"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter description"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.description}
                      rows={4}
                    />
                  </div>
                  <div className="xl:col-span-12">
                    <label className="inline-block mb-2 text-base font-medium">
                      Media File*
                    </label>
                    <div 
                      className="flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-md border-slate-200 dark:border-zink-500"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                    >
                      {imagePreview ? (
                        <div className="relative w-full">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="object-contain w-full max-h-64 mx-auto"
                          />
                          <button
                            type="button"
                            className="absolute p-1 text-white bg-red-500 rounded-full top-2 right-2"
                            onClick={() => {
                              setImagePreview(null);
                              setSelectedFile(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                          >
                            <X className="size-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8">
                          <UploadCloud className="size-12 text-slate-400" />
                          <p className="mt-2 text-sm text-slate-500">
                            Drag and drop a file here or click to browse
                          </p>
                          <button
                            type="button"
                            className="px-4 py-2 mt-4 text-white rounded-md bg-custom-500"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Browse Files
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </div>
                  </div>
                  <div className="xl:col-span-12">
                    <div className="flex justify-end gap-2 mt-4">
                      <Link
                        to="/media"
                        className="text-red-500 bg-red-100 btn hover:text-white hover:bg-red-600 focus:text-white focus:bg-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:ring active:ring-red-100 dark:bg-red-500/20 dark:text-red-500 dark:hover:bg-red-500 dark:hover:text-white dark:focus:bg-red-500 dark:focus:text-white dark:active:bg-red-500 dark:active:text-white dark:ring-red-400/20"
                      >
                        Cancel
                      </Link>
                      <button
                        type="submit"
                        disabled={validation.isSubmitting || loading}
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                      >
                        {validation.isSubmitting || loading ? (
                          <TailSpin
                            visible={true}
                            height="20"
                            width="20"
                            color="#fff"
                            ariaLabel="tail-spin-loading"
                            radius="1"
                            wrapperStyle={{}}
                            wrapperClass=""
                          />
                        ) : isEdit ? (
                          "Update Media"
                        ) : (
                          "Add Media"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default MediaForm;
